import React, { useEffect, useMemo, useState } from 'react';
import { BrainCog, Loader2, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { getAiModelSettings, updateAiModelSettings } from '../../../redux/api';

const PACKAGE_LABELS = {
  1: 'Free',
  2: 'Starter',
  3: 'Standard',
  4: 'Business',
  5: 'Premium'
};

const AISettingsView = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [companyPackage, setCompanyPackage] = useState(1);
  const [strictPhoneVerification, setStrictPhoneVerification] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await getAiModelSettings();
        if (!mounted) return;
        const data = res?.data || {};
        setModels(Array.isArray(data.models) ? data.models : []);
        setSelectedModel(data.selectedModel || '');
        setCompanyPackage(Number(data.package || 1));
        setStrictPhoneVerification(Boolean(data.aiStrictPhoneVerification));
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Ne mogu da učitam AI podešavanja.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const sortedModels = useMemo(
    () => [...models].sort((a, b) => Number(a.strengthRank || 0) - Number(b.strengthRank || 0)),
    [models]
  );

  const saveSettings = async () => {
    if (!selectedModel) return;
    setSaving(true);
    try {
      const res = await updateAiModelSettings({
        aiModel: selectedModel,
        aiStrictPhoneVerification: strictPhoneVerification
      });
      const next = res?.data || {};
      if (Array.isArray(next.models)) {
        setModels(next.models);
      }
      if (next.selectedModel) {
        setSelectedModel(next.selectedModel);
      }
      if (typeof next.aiStrictPhoneVerification === 'boolean') {
        setStrictPhoneVerification(next.aiStrictPhoneVerification);
      }
      toast.success('AI podešavanja su sačuvana.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Ne mogu da sačuvam AI podešavanja.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-900 min-h-screen p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 flex items-start gap-4">
          <div className="bg-cyan-500/20 rounded-lg p-3">
            <BrainCog className="h-8 w-8 text-cyan-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">AI podešavanja</h1>
            <p className="text-sm text-gray-300 mt-2">
              Ovde birate model za AI Import i AI dodavanje posla iz slika. Modeli su poređani od
              najslabijeg ka najjačem.
            </p>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-300">
              <Loader2 className="h-5 w-5 animate-spin" />
              Učitavanje AI podešavanja...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-300">
                Aktivni paket: <span className="font-semibold text-white">{PACKAGE_LABELS[companyPackage] || 'Free'}</span>
              </div>
              <label className="block text-sm text-gray-300">Izaberite model</label>
              <select
                className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-white"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {sortedModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.label} ({model.id})
                  </option>
                ))}
              </select>

              <label className="flex items-start gap-3 rounded-lg border border-gray-700 bg-gray-900/60 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={strictPhoneVerification}
                  onChange={(e) => setStrictPhoneVerification(e.target.checked)}
                />
                <span>
                  <span className="block text-sm font-medium text-white">
                    Stroga AI provera telefona (drugi API poziv)
                  </span>
                  <span className="block text-xs text-gray-400 mt-1">
                    Podrazumevano je isključeno. AI i dalje čita broj dva puta u jednom zahtevu (bez
                    dodatnog troška slika). Uključite ovo samo ako vam treba dodatni, skuplji prolaz
                    sa ponovnim slanjem slika — povećava OpenAI trošak, ali ne troši dodatne slike iz
                    paketa.
                  </span>
                </span>
              </label>

              <button
                type="button"
                onClick={saveSettings}
                disabled={saving || !selectedModel}
                className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-white hover:bg-cyan-700 disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Sačuvaj podešavanja
              </button>
            </div>
          )}
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-3">Dostupni modeli (od najslabijeg do najjačeg)</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-300 border-b border-gray-700">
                  <th className="py-2 pr-4">Model</th>
                  <th className="py-2 pr-4">Jačina</th>
                  <th className="py-2 pr-4">Relativna cena</th>
                  <th className="py-2 pr-4">Predlog priloga po zahtevu</th>
                </tr>
              </thead>
              <tbody>
                {sortedModels.map((model) => (
                  <tr key={model.id} className="border-b border-gray-800 text-gray-200">
                    <td className="py-2 pr-4">{model.label}</td>
                    <td className="py-2 pr-4">#{model.strengthRank}</td>
                    <td className="py-2 pr-4">{model.relativeCost}x</td>
                    <td className="py-2 pr-4">{model.recommendedMaxAttachments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Predlog računanja: skuplji model => manji broj slika po zahtevu, da bi ukupni trošak po
            operaciji ostao stabilan.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AISettingsView;
